import * as stylex from "@stylexjs/stylex";
import {
  colors,
  fonts,
  fontSizes,
  spacing,
} from "@softmax/design-system/tokens.stylex";
import { ExternalMark, externalLinkProps } from "@softmax/web-ux/shell";
import { FitTitle } from "./fit-title";
import { siteDomain } from "./coworld-values";

export function CoworldHeaderName({
  name,
  site,
  anchored,
}: {
  readonly name: string;
  readonly site: string | undefined;
  readonly anchored: boolean;
}) {
  return (
    <div {...stylex.props(styles.nameBlock, anchored && styles.nameTop)}>
      <p {...stylex.props(styles.caption)}>Coworld</p>
      {anchored ? (
        <FitTitle {...stylex.props(styles.name, styles.nameFit)}>
          {name}
        </FitTitle>
      ) : (
        <h1 {...stylex.props(styles.name)}>{name}</h1>
      )}
      {site === undefined ? null : (
        <a
          href={site}
          {...externalLinkProps}
          {...stylex.props(styles.siteLink, styles.narrowSite)}
        >
          {siteDomain(site)}
          <ExternalMark />
        </a>
      )}
    </div>
  );
}

const wide = "@media (min-width: 640px)";

const styles = stylex.create({
  nameBlock: {
    gridColumnStart: 2,
    gridRowStart: { default: 1, [wide]: 2 },
    display: "flex",
    minWidth: 0,
    flexDirection: "column",
    gap: { default: spacing.space4, [wide]: spacing.space0 },
    alignSelf: "center",
  },
  nameTop: { gridColumnStart: 2, gridRowStart: 1, alignSelf: "center" },
  nameFit: {
    lineHeight: 1.15,
    textWrap: "balance",
    overflowWrap: "normal",
    wordBreak: "normal",
    hyphens: "none",
  },
  caption: {
    display: { default: "block", [wide]: "none" },
    margin: 0,
    fontSize: fontSizes.caption,
    lineHeight: "17px",
    fontWeight: 500,
    color: colors.muted,
  },
  name: {
    margin: 0,
    minWidth: 0,
    fontFamily: fonts.mono,
    fontSize: { default: fontSizes.section, [wide]: fontSizes.coworldTitle },
    lineHeight: { default: "30px", [wide]: "52px" },
    fontWeight: 600,
    color: colors.foreground,
    overflowWrap: "break-word",
  },
  siteLink: {
    alignSelf: "flex-start",
    borderRadius: 3,
    fontSize: fontSizes.small,
    lineHeight: "19px",
    color: colors.secondary,
    textDecoration: { default: "none", ":hover": "underline" },
    textDecorationColor: colors.borderStrong,
    textUnderlineOffset: 3,
    ":focus-visible": {
      outlineWidth: 2,
      outlineStyle: "solid",
      outlineColor: colors.foreground,
      outlineOffset: 2,
    },
  },
  narrowSite: { display: { default: "inline", [wide]: "none" } },
});
